import * as React from 'react';
import { StyleSheet } from 'aphrodite';
import { t } from '@citrite/translate';
import {
	ExclamationInTriangleSolidIcon,
	layout,
	NotificationBadgeIcon,
	palette,
	SmallLightText,
	Text,
	Tooltip,
	typography,
} from '@citrite/web-ui-component';
import { SkeletonLoaderShapes } from '@citrite/workspace-ui-platform-react';
import styled from '@emotion/styled';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import { Session, UNKNOWN_STATE } from 'App/Activity/ResourceManagerContext';
import { useResourceActions } from 'App/Activity/useResourceActions';
import { useResourceState } from 'App/Activity/useResourceState';
import { useTransitioningStatus } from 'App/Activity/useTransitioningStatus';
import { getTimestamp } from 'App/Activity/Utility';
import { StyledLoadingDots } from 'App/ProgressAlert/ResourceAlert';
import {
	LaunchProgressStatusResult,
	TROUBLESHOOTING_STATUS,
} from 'App/ProgressAlert/utils';
import { getResourceSubText, tooltipDelay } from 'App/Screen/util';
import { PowerStateIndicator } from 'App/Tile/PowerStateIndicator';
import { TileIcon } from 'App/Tile/TileIcon';
import { Favorite } from 'Components/ResourceTile/Favorite';
import { useBranding } from 'Configuration/withBranding';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import {
	DesktopAssignmentType,
	Resource,
} from 'Workspace/ResourceProvider/resourceTypes';

const favoriteIconStyles = StyleSheet.create({
	icon: {
		padding: `0 ${layout.tinySpace} 0 0`,
	},
	loading: {
		padding: '2px',
	},
});

export const StyledLightText = styled(SmallLightText)`
	color: ${palette.black};
	opacity: 0.6;
	padding: 6px 0;
	font-weight: ${typography.weights.regular};
	line-height: 11px;
	min-width: fit-content;
`;

const StyledTroubleShootStatus = styled(StyledLightText)`
	display: flex;
	align-items: center;
	gap: 6px;
`;

const StyledDiv = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	width: 80%;
	max-width: 80%;
`;

const InnerStyledDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	padding: 0 ${layout.mediumSpace};
	width: 80%;
`;

const StyledNotificationBadge = styled(NotificationBadgeIcon)`
	display: inline-block;
	margin-right: ${layout.tinySpace};
`;

export const SubTextDiv = styled.div<{ isSmallFormFactor: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	width: 100%;
	${props =>
		props.isSmallFormFactor &&
		`
		justify-content: flex-start;	
	`}
`;

export const StyledText = styled(Text)<{ isSmallFormFactor: boolean }>`
	text-align: center;
	font-weight: ${typography.weights.semiBold};
	color: ${palette.black};
	height: ${layout.largeSpace};
	max-height: ${layout.largeSpace};
	overflow: hidden;
	margin-top: ${layout.mediumSpace};
	width: 100%;
	line-height: ${layout.mediumSpace};
	${props =>
		props.isSmallFormFactor
			? `
		margin: 0;
		display: block;
		text-align: start;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		width: 100%;
		height: 100%;
	`
			: `
		text-overflow: ellipsis;
		-webkit-line-clamp: 2;
  		height: inherit;
	    display: -webkit-box;
		-webkit-box-orient: vertical;
		word-break: break-word;
	`};
`;

const SmallFactorFavoriteWrapperLegacy = styled.div`
	display: flex;
`;

const SmallFactorFavoriteWrapperDetailed = styled.div`
	padding-bottom: ${layout.tinySpace};
	margin-right: ${layout.tinySpace};
`;

interface Props {
	resource: Resource;
	inputRef?: React.RefObject<HTMLInputElement>;
	isSmallFormFactor: boolean;
	isResourceAvailable: boolean;
	hasSubscriptionsEnabled: boolean;
	canShowTroubleshootStatus: boolean;
}

export enum ResourceType {
	WebApp = 'Citrix.MPS.Document',
	VirtualApp = 'Citrix.MPS.Application',
	Desktop = 'Citrix.MPS.Desktop',
}

export const Container = ({ children, isSmallFormFactor }: any) =>
	isSmallFormFactor ? <StyledDiv>{children}</StyledDiv> : <>{children}</>;

export const InnerContainer = ({ isSmallFormFactor, children }: any) =>
	isSmallFormFactor ? <InnerStyledDiv>{children}</InnerStyledDiv> : <>{children}</>;

const { LoadingBar } = SkeletonLoaderShapes;

function ResourceDetails({
	resource,
	inputRef,
	isSmallFormFactor,
	isResourceAvailable,
	hasSubscriptionsEnabled,
	canShowTroubleshootStatus,
}: Props) {
	const resourceName = <>{resource.name}</>;
	const isNewResourceHighlightEnabled = useFeatureCanary(
		FeatureFlag.HighlightNewResource
	);
	const branding = useBranding();
	const resourceState = useResourceState(resource);
	const isMachinePowerStateEnabled = useFeatureCanary(
		FeatureFlag.EnableMachinePowerState
	);
	const isCenterAlignClassicViewEnabled = useFeatureCanary(
		FeatureFlag.CenterAlignClassicView
	);

	const resourceSession = useResourceActions(resource);
	const transitioningStatus = useTransitioningStatus(
		resourceSession.session as Session,
		resource
	);

	const highlightNewResource =
		!isSmallFormFactor && isNewResourceHighlightEnabled && !!resource?.isnewresource;

	const SmallFactorFavoriteWrapper = isCenterAlignClassicViewEnabled
		? SmallFactorFavoriteWrapperDetailed
		: SmallFactorFavoriteWrapperLegacy;

	const displayResourceType = (currentResource: Resource) => {
		if (isMachinePowerStateEnabled && currentResource?.type === ResourceType.Desktop) {
			return (
				<Tooltip
					hoverTrigger={
						<StyledLightText data-testid="resource-type">
							{getResourceSubText(currentResource?.type)}
						</StyledLightText>
					}
				>
					{currentResource.desktopassignmenttype === DesktopAssignmentType.Shared
						? t('Workspace:activity_manager.tooltip_pooled_desktop')
						: t('Workspace:activity_manager.tooltip_state_unavailable')}
				</Tooltip>
			);
		} else {
			return (
				<StyledLightText data-testid="resource-type">
					{getResourceSubText(currentResource?.type)}
				</StyledLightText>
			);
		}
	};

	function isLaunchStatus(value: string): value is LaunchProgressStatusResult {
		if (value === TROUBLESHOOTING_STATUS) {
			return true;
		}
		return Object.values(LaunchStatus).includes(value as LaunchStatus);
	}

	const getResourceStatus = () => {
		if (canShowTroubleshootStatus) {
			return (
				<Tooltip
					hoverTrigger={
						<StyledTroubleShootStatus>
							<ExclamationInTriangleSolidIcon color={palette.red800} size={12} />
							{t('Workspace:inspect_connection.troubleshoot')}
						</StyledTroubleShootStatus>
					}
				>
					{t('Workspace:inspect_connection.troubleshoot_tooltip')}
				</Tooltip>
			);
		}
		if (
			!isMachinePowerStateEnabled ||
			resourceState?.state === PowerState.NOT_SUPPORTED ||
			!resource?.canquerymachinestate
		) {
			return displayResourceType(resource);
		} else if (resourceState?.state === PowerState.LOADING) {
			return (
				<LoadingBar
					width="80px"
					topMargin={layout.smallSpace}
					bottomMargin={layout.smallSpace}
				/>
			);
		} else if (transitioningStatus !== UNKNOWN_STATE) {
			return (
				<StyledLightText data-testid="resource-transitioning-status">
					{isLaunchStatus(transitioningStatus) ? (
						<>
							{t(`Workspace:launch_status.${transitioningStatus}`)}
							<StyledLoadingDots />
						</>
					) : (
						<>
							{transitioningStatus.replace('...', '')}
							<StyledLoadingDots />
						</>
					)}
				</StyledLightText>
			);
		} else {
			const lastUpdatedTimestamp = getTimestamp(resourceState?.lastUpdatedTime);
			return (
				<PowerStateIndicator
					resourceType={resource.type}
					powerState={resourceState?.state}
					lastUpdatedTime={lastUpdatedTimestamp}
					isSmallFormFactor={isSmallFormFactor}
				/>
			);
		}
	};

	return (
		<Container isSmallFormFactor={isSmallFormFactor}>
			<TileIcon resource={resource} />
			<InnerContainer isSmallFormFactor={isSmallFormFactor}>
				<StyledText isSmallFormFactor={isSmallFormFactor}>
					{highlightNewResource && (
						<StyledNotificationBadge
							data-testid="new-app-notifier"
							color={branding.styles.AccentColor}
						/>
					)}
					{isResourceAvailable ? (
						<Tooltip hoverTrigger={resourceName} delay={tooltipDelay}>
							{resourceName}
						</Tooltip>
					) : (
						resourceName
					)}
				</StyledText>
				<SubTextDiv isSmallFormFactor={isSmallFormFactor}>
					{isSmallFormFactor && isResourceAvailable && hasSubscriptionsEnabled && (
						<SmallFactorFavoriteWrapper
							data-testid="small-factor-favorite-wrapper"
							onClick={e => e.stopPropagation()}
						>
							<Favorite
								{...resource}
								iconSize={8}
								styles={favoriteIconStyles}
								inputRef={inputRef}
							/>
						</SmallFactorFavoriteWrapper>
					)}
					{getResourceStatus()}
				</SubTextDiv>
			</InnerContainer>
		</Container>
	);
}

export const ResourceDetailsWithFavoriteRef = React.forwardRef(
	(props: Props, ref: React.RefObject<HTMLInputElement>) => (
		<ResourceDetails {...props} inputRef={ref} />
	)
);
